import React, { useState, useEffect } from "react";
import Button from "react-bootstrap/Button";
import noImage from "../../assets/icons/svg/no-image.svg";
import GeneralPlayerWrapper from "../Player/GeneralPlayerWrapper";
import { Modal, Tooltip } from "..";
import { updateModal } from "@/store/app/app.slice";
import {
  Trash,
  Play,
  SquarePen,
  Database,
  Cog,
  Send,
  Palette,
} from "lucide-react";
import { connect } from "react-redux";
import { ConnectedProps } from "react-redux";
import { RootState } from "../../store";
import { getMedia } from "@/store/media/media.action";
import { setPreviewMediaDetails } from "@/store/media/media.slice";
import { MediaModel } from "@/database";
import { truncateText } from "@/utils/utilFunctions/truncateText";

interface SelectedMediaCardProps {
  media: {
    label: string;
    value: string;
    image: string;
    duration: string;
    thumb: boolean;
  };
  type: "pre" | "post";
  onClose: () => void;
  onEdit?: () => void;
  onPreview?: () => void;
  showEditButton?: boolean;
  showPreviewButton?: boolean;
  maxLength?: number;
  disableEdit?: boolean;
  style?: React.CSSProperties;
  cardStyle?: React.CSSProperties;
  showImage?: boolean;
  onTitleClick?: () => void;
  showProduct?: boolean;
  team?: any;
  showDeleteButton?: boolean;
  presetType?: "meta" | "processing" | "social" | "branding";
  readOnlyMode?: boolean;
  isPreview?: boolean;
}

const SelectedMediaCard = ({
  media,
  type,
  onClose,
  onEdit,
  onPreview,
  showEditButton = false,
  showPreviewButton = false,
  maxLength = 50,
  disableEdit = false,
  style = {},
  cardStyle = {},
  showImage = true,
  onTitleClick,
  showProduct = false,
  team,
  showDeleteButton = true,
  presetType,
  readOnlyMode = false,
  getMedia,
  previewMediaDetails,
  isPreview = true,
  setPreviewMediaDetails,
}: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPreviewOn, setIsPreviewOn] = useState(false);
  const [isValidImage, setIsValidImage] = useState(true);

  useEffect(() => {
    if (media.label && isPreview && isPreviewOn) {
      setPreviewMediaDetails({
        isLoading: true,
        data: { media: {} as MediaModel },
      });
      getMedia({ id: media.label, type: "preview" });
      setIsModalOpen(true);
    }
  }, [media.label, isPreviewOn]);

  const handlePreview = () => {
    setIsPreviewOn(true);
  };

  const getPresetIcon = (presetType?: string) => {
    switch (presetType) {
      case "meta":
        return <Database size={16} className="text-secondary mb-auto" />;
      case "processing":
        return <Cog size={16} className="text-secondary mb-auto" />;
      case "social":
        return <Send size={16} className="text-secondary mb-auto" />;
      case "branding":
        return <Palette size={16} className="text-secondary mb-auto" />;
      default:
        return null;
    }
  };

  return (
    <div className="card bg-light d-flex flex-row gap-2 p-2">
      {showImage && (
        <div className="position-relative">
          <div
            className="rounded ratio ratio-16x9 border bg-secondary pointer"
            style={{
              width: cardStyle.width || "120px",
              minWidth: cardStyle.minWidth || "120px",
              cursor: "pointer",
              overflow: "hidden",
            }}
            onMouseEnter={(e) => {
              const overlay = e.currentTarget.querySelector(
                ".media-hover-overlay"
              ) as HTMLElement;
              if (overlay) overlay.style.opacity = "1";
            }}
            onMouseLeave={(e) => {
              const overlay = e.currentTarget.querySelector(
                ".media-hover-overlay"
              ) as HTMLElement;
              if (overlay) overlay.style.opacity = "0";
            }}
            onClick={handlePreview}
          >
            <img
              src={media?.thumb && isValidImage ? media?.image : noImage}
              alt={media?.label}
              className="object-fit-cover position-absolute top-50 start-50 translate-middle"
              style={{ width: "100%", height: "100%" }}
              onError={() => setIsValidImage(false)}
            />
            {showPreviewButton && (
              <div
                className="media-hover-overlay play-button"
                // style={{
                //   opacity: 0,
                //   transition: 'opacity 0.3s ease-in-out',
                //   pointerEvents: 'none', // ensures the click still goes to the parent
                // }}
              >
                <i
                  className="bi bi-play-fill text-white"
                  style={{ fontSize: "2rem" }}
                ></i>
              </div>
            )}

            <span
              className="timeCard position-absolute end-0 bottom-0"
              // style={{ bottom: -1, right: -1 }}
            >
              {media?.duration}
            </span>
          </div>
        </div>
      )}

      <div
        className="w-100"
        onClick={(e) => {
          e.stopPropagation();
          onTitleClick?.();
        }}
      >
        <div className="d-flex gap-1">
          <div className="d-flex align-items-center gap-2">
            {/* {presetType && (
              <Tooltip
                content={<p className="p-0 mb-0">{presetType.charAt(0).toUpperCase() + presetType.slice(1)} Preset</p>}
              >
                {getPresetIcon(presetType)}
              </Tooltip>
            )} */}
            <Tooltip
              content={<p className="p-0 mb-0">{String(media?.value)}</p>}
            >
              <span
                className="line-clamp-2 pointer anchor"
                onClick={onPreview ?? handlePreview}
                //  style={{ cursor: onTitleClick ? 'pointer' : 'default' }}
              >
                {truncateText(String(media?.value), maxLength)}
              </span>
            </Tooltip>
          </div>
          <div
            className="ms-auto position-absolute"
            style={{ top: "6px", right: "0" }}
          >
            {showEditButton && onEdit && !disableEdit && (
              <Tooltip content={<p className="p-0 mb-0">Change Media</p>}>
                <Button
                  variant="outline-primary"
                  onClick={onEdit}
                  title="Edit"
                  className="border-0 p-2 H"
                >
                  <i
                    className=""
                    style={{
                      width: "24px",
                      height: "24px",
                      fontSize: "14px",
                      cursor: "pointer",
                      zIndex: 2,
                    }}
                    title="Edit"
                  ></i>{" "}
                </Button>
              </Tooltip>
            )}
            {showPreviewButton && !disableEdit && (
              <Tooltip content={<p className="p-0 mb-0 H">Preview Media</p>}>
                <Button
                  variant="outline-primary"
                  onClick={onPreview ?? handlePreview}
                  title="Preview"
                  className="border-0 p-1 w-100 border-end H"
                >
                  <Play size={16} />
                </Button>
              </Tooltip>
            )}
            {!disableEdit && showDeleteButton && (
              <Tooltip content={<p className="p-0 mb-0">Change Media</p>}>
                <Button
                  variant="outline-primary"
                  onClick={onClose}
                  title="Edit"
                  className="border-0 w-100"
                >
                  <SquarePen size={14} />
                </Button>
              </Tooltip>
            )}
          </div>
        </div>
        {!showProduct ? (
          <small className="text-muted">ID: {media?.label}</small>
        ) : (
          <small className="text-muted ms-4 H">team: {team}</small>
        )}
      </div>

      {isModalOpen && !previewMediaDetails?.isLoading && (
        <Modal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setIsPreviewOn(false);
          }}
          title={previewMediaDetails.media?.title}
          closeOnOutsideClick={true}
          width="990px"
          height="auto"
          zIndex={1070}
          updateModal={updateModal}
        >
          <GeneralPlayerWrapper
            media={{
              ...previewMediaDetails.media,
            }}
            showDetails={true}
          />
        </Modal>
      )}
    </div>
  );
};

const mapState = (state: RootState) => ({
  previewMediaDetails: state.media.previewMediaDetails,
});

const mapDispatch = {
  getMedia,
  setPreviewMediaDetails,
};

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector> & SelectedMediaCardProps;
export default connector(SelectedMediaCard);
